// Block/item IDs reference:
// 5=Wood, 8=Cobblestone, 9=Planks, 4=Sand, 10=Glass, 11=Brick
// 19=Stick, 20=Torch, 21=Iron Ingot, 22=Gold Ingot, 23=Diamond
// 26=Wood Pickaxe, 27=Iron Pickaxe, 28=Coal
// 29=Zandsteen, 30=IJs, 43=Klei, 44=Vliegblok, 45=Supervliegblok
// 46=Portaalsteen, 48=Bed
// 50=Springplank, 51-54=Verfblokken, 55=Pijlblok, 59=Muziekblok
// 60=Teleporteerblok, 61=Explosiefblok, 62=Vuurwerk, 63=Snelheidsblok

const _ = 0; // empty slot shorthand

export interface Recipe {
  pattern: number[][]; // 2D grid (1x1, 2x2, or 3x3)
  result: number;      // item id produced
  count: number;       // how many produced
}

// Recipes are defined with smallest bounding box
export const RECIPES: Recipe[] = [
  // 1 Wood → 4 Planks
  { pattern: [[5]], result: 9, count: 4 },

  // 2 Planks (vertical) → 4 Sticks
  { pattern: [[9], [9]], result: 19, count: 4 },

  // 1 Stick + 1 Coal (vertical) → 4 Torches
  { pattern: [[28], [19]], result: 20, count: 4 },

  // Wooden Pickaxe: 3 Planks top + 2 Sticks
  { pattern: [[9, 9, 9], [_, 19, _], [_, 19, _]], result: 26, count: 1 },

  // Iron Pickaxe: 3 Iron top + 2 Sticks
  { pattern: [[21, 21, 21], [_, 19, _], [_, 19, _]], result: 27, count: 1 },

  // 4 Cobblestone (2x2) → 4 Bricks
  { pattern: [[8, 8], [8, 8]], result: 11, count: 4 },

  // 4 Sand (2x2) → 4 Zandsteen
  { pattern: [[4, 4], [4, 4]], result: 29, count: 4 },

  // 4 Klei (2x2) → 4 Baksteen
  { pattern: [[43, 43], [43, 43]], result: 11, count: 4 },

  // 4 Glass (2x2) → 4 IJs
  { pattern: [[10, 10], [10, 10]], result: 30, count: 4 },

  // 1 Grass + 1 Plank → 1 Vliegblok
  { pattern: [[1], [9]], result: 44, count: 1 },

  // 1 Diamond + 1 Vliegblok → 1 Supervliegblok
  { pattern: [[23], [44]], result: 45, count: 1 },

  // 3 Cobblestone + 1 Coal (2x2) → 4 Portaalsteen
  { pattern: [[8, 8], [8, 28]], result: 46, count: 4 },

  // 3 Planks (horizontal row) → 1 Bed
  { pattern: [[9, 9, 9]], result: 48, count: 1 },

  // 1 Iron Ingot + 1 Grass (vertical) → 1 Snelheidsblok
  { pattern: [[21], [1]], result: 63, count: 1 },

  // 2 Iron Ingots + 1 Cobblestone → 4 Pijlblok
  { pattern: [[21, 21], [8, _]], result: 55, count: 4 },

  // 1 Diamond + 1 Supervliegblok (vertical) → 1 Springplank
  { pattern: [[23], [45]], result: 50, count: 1 },

  // 1 Cobblestone + 1 Flower → 2 Verfblok Rood
  { pattern: [[8, 13]], result: 51, count: 2 },

  // 1 Cobblestone + 1 Diamond → 2 Verfblok Blauw
  { pattern: [[8, 23]], result: 52, count: 2 },

  // 1 Cobblestone + 1 Leaves → 2 Verfblok Groen
  { pattern: [[8, 6]], result: 53, count: 2 },

  // 1 Cobblestone + 1 Gold Ingot → 2 Verfblok Geel
  { pattern: [[8, 22]], result: 54, count: 2 },

  // 2 Planks + 1 Gold Ingot → 1 Muziekblok
  { pattern: [[9, 9], [_, 22]], result: 59, count: 1 },

  // 1 Diamond + 1 Portaalsteen (vertical) → 1 Teleporteerblok
  { pattern: [[23], [46]], result: 60, count: 1 },

  // 1 Stick + 1 Coal + 1 Flower (vertical) → 2 Vuurwerk
  { pattern: [[19], [28], [13]], result: 62, count: 2 },

  // 3x3 Sand with Coal center → 1 Explosiefblok
  { pattern: [[4, 4, 4], [4, 28, 4], [4, 4, 4]], result: 61, count: 1 },
];

/**
 * Try to match a 3x3 crafting grid against all recipes.
 * The grid is [row][col], where 0 = empty.
 */
/**
 * Get all unique ingredients needed for a recipe
 */
export function getRecipeIngredients(recipe: Recipe): number[] {
  const ingredients = new Set<number>();
  for (const row of recipe.pattern) {
    for (const cell of row) {
      if (cell !== 0) ingredients.add(cell);
    }
  }
  return [...ingredients];
}

/**
 * Check if player can craft a recipe (has all ingredients)
 */
export function canCraftRecipe(recipe: Recipe, inventory: Map<number, number>): boolean {
  // Count required ingredients
  const required = new Map<number, number>();
  for (const row of recipe.pattern) {
    for (const cell of row) {
      if (cell !== 0) {
        required.set(cell, (required.get(cell) || 0) + 1);
      }
    }
  }
  // Check if inventory has enough
  for (const [itemId, count] of required) {
    if ((inventory.get(itemId) || 0) < count) return false;
  }
  return true;
}

/**
 * Get recipe book data: all recipes with craftability status
 */
export function getRecipeBook(inventory: Map<number, number>): {
  recipe: Recipe;
  canCraft: boolean;
  ingredients: number[];
}[] {
  return RECIPES.map(recipe => ({
    recipe,
    canCraft: canCraftRecipe(recipe, inventory),
    ingredients: getRecipeIngredients(recipe),
  }));
}

/**
 * Try to match a 3x3 crafting grid against all recipes.
 * The grid is [row][col], where 0 = empty.
 */
export function matchRecipe(grid: number[][]): { itemId: number; count: number } | null {
  // Find bounding box of non-empty cells
  let minR = 3, maxR = -1, minC = 3, maxC = -1;
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      if (grid[r][c] !== 0) {
        if (r < minR) minR = r;
        if (r > maxR) maxR = r;
        if (c < minC) minC = c;
        if (c > maxC) maxC = c;
      }
    }
  }

  // Empty grid
  if (maxR === -1) return null;

  const height = maxR - minR + 1;
  const width = maxC - minC + 1;

  // Extract the sub-grid
  const sub: number[][] = [];
  for (let r = 0; r < height; r++) {
    sub[r] = [];
    for (let c = 0; c < width; c++) {
      sub[r][c] = grid[minR + r][minC + c];
    }
  }

  // Match against recipes
  for (const recipe of RECIPES) {
    const pH = recipe.pattern.length;
    const pW = recipe.pattern[0].length;

    if (pH !== height || pW !== width) continue;

    let match = true;
    for (let r = 0; r < pH && match; r++) {
      for (let c = 0; c < pW && match; c++) {
        if (sub[r][c] !== recipe.pattern[r][c]) {
          match = false;
        }
      }
    }

    if (match) {
      return { itemId: recipe.result, count: recipe.count };
    }
  }

  return null;
}
